import { Link } from "react-router-dom";
import { SiteLayout } from "../components/layout/SiteLayout";
import { WhyEcosystemDiagram } from "../components/marketing/WhyEcosystemDiagram";
import { ProductStatsBar } from "../components/product/ProductStatsBar";
import { hubPageJsonLd, SeoHead } from "../components/seo/SeoHead";
import { PublisherTrustStrip } from "../components/trust/InstitutionalTrust";
import { DomainIcon } from "../home/HomeIcons";
import { HOME_DOMAINS } from "../home/home-content";
import { usePepI18n } from "../i18n/usePepI18n";
import { SECTORS_PRIMARY } from "../sectors/sectors-content";
import {
  WHY_ANSWER_POINTS,
  WHY_BENEFITS,
  WHY_DIFFERENTIATORS,
  WHY_HERO_KPIS,
  WHY_PROBLEMS,
  WHY_RDC_POINTS,
  whyCopy,
} from "../why/why-content";

/**
 * Why ITaaS — conviction page.
 * Composes existing PEP patterns (page hero, home domains, sol diagram, cta-band, trust strip).
 */
export function WhyItaasPage() {
  const { locale } = usePepI18n();
  const en = locale === "en";
  const copy = whyCopy(locale);

  return (
    <SiteLayout>
      <SeoHead
        title={copy.seoTitle}
        description={copy.seoDescription}
        keywords={copy.seoKeywords}
        canonicalPath="/why-itaas"
        locale={locale}
        jsonLd={hubPageJsonLd({
          canonicalPath: "/why-itaas",
          name: copy.seoTitle,
          description: copy.seoDescription,
        })}
      />

      {/* W-HERO — Enterprise Premium (Product Page CTA + ecosystem + KPI) */}
      <header className="pep-page-hero pep-why-hero py-5 mb-0">
        <div className="container">
          <div className="row align-items-center g-4 g-xl-5">
            <div className="col-lg-7 pep-why-hero__copy">
              <p className="pep-why-eyebrow mb-2">{copy.heroEyebrow}</p>
              <h1 className="display-5 fw-bold mb-3 pep-why-hero__title">
                {copy.heroH1Lead}{" "}
                <span className="pep-why-hero__emphasis">{copy.heroH1Emphasis}</span>
              </h1>
              <p className="lead opacity-90 mb-4">{copy.heroLead}</p>

              <div className="pep-why-hero__ctas mb-4">
                <Link className="btn pep-pd-btn-primary pep-why-hero__cta-primary" to={copy.heroPrimaryHref}>
                  {copy.heroPrimaryCta}
                  <span className="pep-why-hero__cta-arrow" aria-hidden="true">
                    →
                  </span>
                </Link>
                <Link className="btn pep-pd-btn-ghost pep-why-hero__cta-secondary" to={copy.heroSecondaryHref}>
                  {copy.heroSecondaryCta}
                </Link>
              </div>

              <div className="pep-why-hero__kpis">
                <ProductStatsBar stats={[...WHY_HERO_KPIS]} locale={locale} />
              </div>
            </div>

            <div className="col-lg-5 pep-why-hero__visual-col">
              <figure className="pep-why-hero__visual mb-0">
                <WhyEcosystemDiagram centerLabel={copy.platformCenter} locale={locale} />
                <figcaption className="visually-hidden">{copy.platformCaption}</figcaption>
              </figure>
            </div>
          </div>
        </div>
      </header>

      {/* W-PROBLEMS */}
      <section
        className="pep-why-section pep-why-section--muted"
        aria-labelledby="why-problems-h2"
        data-section="W-PROBLEMS"
      >
        <div className="container">
          <header className="pep-why-section__head">
            <p className="pep-why-eyebrow pep-why-eyebrow--ink">{copy.problemsEyebrow}</p>
            <h2 id="why-problems-h2" className="pep-why-h2">
              {copy.problemsH2}
            </h2>
            <p className="pep-why-lead">{copy.problemsLead}</p>
          </header>
          <ul className="pep-why-problem-grid list-unstyled mb-0">
            {WHY_PROBLEMS.map((item) => (
              <li key={item.id} className="pep-why-problem-card">
                <span className="pep-why-problem-card__mark" aria-hidden="true">
                  ×
                </span>
                <h3 className="pep-why-problem-card__title">
                  {en ? item.titleEn : item.titleFr}
                </h3>
                <p className="pep-why-problem-card__body mb-0">
                  {en ? item.bodyEn : item.bodyFr}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* W-ANSWER */}
      <section
        className="pep-why-section"
        aria-labelledby="why-answer-h2"
        data-section="W-ANSWER"
      >
        <div className="container">
          <div className="row g-4 g-lg-5 align-items-center">
            <div className="col-lg-5">
              <p className="pep-why-eyebrow pep-why-eyebrow--ink">{copy.answerEyebrow}</p>
              <h2 id="why-answer-h2" className="pep-why-h2">
                {copy.answerH2}
              </h2>
              <p className="pep-why-lead mb-0">{copy.answerLead}</p>
            </div>
            <div className="col-lg-7">
              <ul className="pep-why-answer-grid list-unstyled mb-0">
                {WHY_ANSWER_POINTS.map((item) => (
                  <li key={item.id} className="pep-why-answer-card">
                    <span className="pep-why-answer-card__mark" aria-hidden="true">
                      ✓
                    </span>
                    <h3 className="pep-why-answer-card__title">
                      {en ? item.titleEn : item.titleFr}
                    </h3>
                    <p className="pep-why-answer-card__body mb-0">
                      {en ? item.bodyEn : item.bodyFr}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* W-DIFFERENT */}
      <section
        className="pep-why-section pep-why-section--mist"
        aria-labelledby="why-different-h2"
        data-section="W-DIFFERENT"
      >
        <div className="container">
          <header className="pep-why-section__head pep-why-section__head--center">
            <p className="pep-why-eyebrow pep-why-eyebrow--ink">{copy.differentEyebrow}</p>
            <h2 id="why-different-h2" className="pep-why-h2">
              {copy.differentH2}
            </h2>
            <p className="pep-why-lead">{copy.differentLead}</p>
          </header>
          <ul className="pep-why-diff-grid list-unstyled mb-0">
            {WHY_DIFFERENTIATORS.map((item) => (
              <li key={item.id} className="pep-why-diff-card">
                <h3 className="pep-why-diff-card__title">
                  {en ? item.titleEn : item.titleFr}
                </h3>
                <p className="pep-why-diff-card__body mb-0">
                  {en ? item.bodyEn : item.bodyFr}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* W-BENEFITS */}
      <section
        className="pep-why-section"
        aria-labelledby="why-benefits-h2"
        data-section="W-BENEFITS"
      >
        <div className="container">
          <header className="pep-why-section__head pep-why-section__head--center">
            <p className="pep-why-eyebrow pep-why-eyebrow--ink">{copy.benefitsEyebrow}</p>
            <h2 id="why-benefits-h2" className="pep-why-h2">
              {copy.benefitsH2}
            </h2>
            <p className="pep-why-lead">{copy.benefitsLead}</p>
          </header>
          <ul className="pep-why-benefit-grid list-unstyled mb-3">
            {WHY_BENEFITS.map((item) => (
              <li key={item.id} className="pep-why-benefit-card">
                <span className="pep-why-benefit-card__metric" aria-hidden="true">
                  {item.metric}
                </span>
                <h3 className="pep-why-benefit-card__title">
                  {en ? item.labelEn : item.labelFr}
                </h3>
                <p className="pep-why-benefit-card__note mb-0">
                  {en ? item.noteEn : item.noteFr}
                </p>
              </li>
            ))}
          </ul>
          <p className="pep-why-disclaimer text-center mb-0">{copy.benefitsDisclaimer}</p>
        </div>
      </section>

      {/* W-SECTORS — reuse HOME_DOMAINS cards + SECTORS_PRIMARY links */}
      <section
        className="pep-why-section pep-why-section--muted"
        aria-labelledby="why-sectors-h2"
        data-section="W-SECTORS"
      >
        <div className="container">
          <header className="pep-why-section__head">
            <p className="pep-why-eyebrow pep-why-eyebrow--ink">{copy.sectorsEyebrow}</p>
            <h2 id="why-sectors-h2" className="pep-why-h2">
              {copy.sectorsH2}
            </h2>
            <p className="pep-why-lead">{copy.sectorsLead}</p>
          </header>

          <ul className="pep-home-domain-grid list-unstyled mb-4">
            {HOME_DOMAINS.map((domain) => (
              <li key={domain.id}>
                <Link
                  className={`pep-home-domain-card pep-home-domain-card--${domain.id}`}
                  to={domain.href}
                >
                  <span className="pep-home-domain-card__icon">
                    <DomainIcon name={domain.icon} />
                  </span>
                  <span className="pep-home-domain-card__title">
                    {en ? domain.titleEn : domain.titleFr}
                  </span>
                  <span className="pep-home-domain-card__product">
                    {en ? domain.productHintEn : domain.productHintFr}
                  </span>
                  <span className="pep-home-domain-card__text">
                    {en ? domain.outcomeEn : domain.outcomeFr}
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          <ul className="pep-why-sector-chips list-unstyled mb-4">
            {SECTORS_PRIMARY.map((sector) => (
              <li key={sector.id}>
                <Link
                  className={`pep-why-sector-chip pep-why-sector-chip--${sector.tone}`}
                  to={`/industries/${sector.industrySlug}`}
                >
                  {en ? sector.titleEn : sector.titleFr}
                </Link>
              </li>
            ))}
          </ul>

          <div className="text-center">
            <Link className="btn btn-outline-primary" to={copy.sectorsHref}>
              {copy.sectorsCta} →
            </Link>
          </div>
        </div>
      </section>

      {/* W-RDC */}
      <section
        className="pep-why-section"
        aria-labelledby="why-rdc-h2"
        data-section="W-RDC"
      >
        <div className="container">
          <div className="row g-4 g-lg-5">
            <div className="col-lg-4">
              <p className="pep-why-eyebrow pep-why-eyebrow--ink">{copy.rdcEyebrow}</p>
              <h2 id="why-rdc-h2" className="pep-why-h2">
                {copy.rdcH2}
              </h2>
              <p className="pep-why-lead">{copy.rdcLead}</p>
              <Link className="pep-home-text-link" to={copy.rdcAboutHref}>
                {copy.rdcAboutCta} →
              </Link>
            </div>
            <div className="col-lg-8">
              <ul className="pep-why-rdc-grid list-unstyled mb-0">
                {WHY_RDC_POINTS.map((item) => (
                  <li key={item.id} className="pep-why-rdc-card">
                    <h3 className="pep-why-rdc-card__title">
                      {en ? item.titleEn : item.titleFr}
                    </h3>
                    <p className="pep-why-rdc-card__body mb-0">
                      {en ? item.bodyEn : item.bodyFr}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* W-FINAL — existing pep-cta-band */}
      <section className="pep-why-section pep-why-section--final" aria-labelledby="why-final-h2">
        <div className="container">
          <div className="p-4 p-md-5 rounded-4 pep-cta-band text-white">
            <div className="row align-items-center g-3">
              <div className="col-lg-7">
                <h2 id="why-final-h2" className="h3 fw-bold mb-2">
                  {copy.finalH2}
                </h2>
                <p className="opacity-90 mb-0">{copy.finalLead}</p>
              </div>
              <div className="col-lg-5 d-flex flex-wrap gap-2 justify-content-lg-end">
                <Link className="btn btn-light btn-lg" to={copy.finalPrimaryHref}>
                  {copy.finalPrimaryCta}
                </Link>
                <Link className="btn btn-outline-light btn-lg" to={copy.finalSecondaryHref}>
                  {copy.finalSecondaryCta}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container py-4">
        <PublisherTrustStrip compact />
      </div>
    </SiteLayout>
  );
}
